/**
 * TOSS Web Application — Client portal: cache edits shared by the chat hooks and the realtime layer
 */

import type { QueryClient } from '@tanstack/react-query';
import type { ClientChatListResponse } from '@/api/clientPortal/clientPortal.types';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';

/**
 * Cut one chat's row out of every cached chat list, synchronously, and take its unread count off
 * the company total.
 *
 * For a chat the caller no longer has — deleted by them, by a colleague, or «for me» on another
 * tab. A refetch alone is too late: the page navigates to «Сообщения» the moment the chat goes,
 * and on a computer it then opens the remembered conversation if the list still has it. With only
 * an invalidation in flight the list still had it — the chat just deleted — and the cabinet
 * reopened it (stand, 30.09.2026). Callers still invalidate the lists afterwards.
 *
 * Only an id is read here, as a key to remove by; nothing from a hub payload is written into the
 * cache, which is the rule `useClientChatRealtime` exists to keep.
 */
export function removeChatFromCachedLists(queryClient: QueryClient, chatId: string): void {
  queryClient.setQueriesData<ClientChatListResponse>(
    { queryKey: clientKeys.chat.lists() },
    (data) => {
      if (!data) return data;
      const gone = data.items.find((item) => item.id === chatId);
      if (!gone) return data;
      return {
        ...data,
        items: data.items.filter((item) => item.id !== chatId),
        totalUnreadCount: Math.max(0, data.totalUnreadCount - gone.unreadCount),
      };
    }
  );
}
